//@flow
import React, {useState} from 'react';
import {ButtonBase, makeStyles, Typography} from '@material-ui/core';
import {colors} from '../../../../../../../../assets/styles/colors';
import FileTypeIcons from '../../../../../../../../elements/FileTypeIcons';
import {formatBits} from '../../../../../../../../utils/number';
import {useCreateObjectUrl} from '../../../../../../../../utils/react';
import DownloadIcon from '../../../../../../../../elements/icons/DownloadIcon';
import {InfoOutlined} from '@material-ui/icons';
import CustomModal from '../../../../../../../../elements/CustomModal/CustomModal';

type Props = {
  name?: string,
  size?: number,
  type?: string,
  self?: boolean,
  isUnreachable?: boolean,
  src: string | Blob | File,
};

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    display: 'flex',
    gap: '.25rem',
    alignItems: 'center',
    minWidth: '250px',

    '&[data-self="true"]': {
      flexDirection: 'row-reverse',
    },
  },
  fileButton: {
    textDecoration: 'none',
    maxWidth: '100%',
    minWidth: 'min-content',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '.5rem',
    borderRadius: '.75rem',
    background: colors.grey200,
    color: colors.grey800,
    gap: '.5rem',
  },
  fileWrap: {
    width: '100%',
    display: 'flex',
    background: colors.grey300,
    gap: '.5rem',
    padding: '.5rem',
    alignItems: 'center',
    borderRadius: '.75rem',
  },
  fileName: {
    fontSize: '.8rem',
    fontWeight: 500,
    flexGrow: 1,
    width: '100%',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fileSize: {
    fontSize: '.75rem',
    fontWeight: 400,
    color: colors.grey600,
  },
  downloadButton: {
    border: 'none',
    cursor: 'pointer',
    background: 'none',

    '&[data-success="true"]': {
      color: colors.success,
    },
  },
  downloadIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    fontSize: '1.5rem',
  },
  fileData: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '1rem',
  },
  fileType: {
    aspectRatio: '1/1',
    borderRadius: '.35rem',
    fontSize: '1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  infoIcon: {
    padding: '.25rem',
    borderRadius: '50%',
    color: colors.grey500,
  },
  modalContainer: {
    maxWidth: '320px',
    padding: '1rem 0',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '.5rem',
  },
  modalTitle: {
    fontSize: '1rem',
    fontWeight: 500,
    color: colors.grey800,
    textAlign: 'center',
  },
  modalText: {
    textAlign: 'center',
    fontSize: '.8rem',
    fontWeight: 400,
    color: colors.grey600,
  },
}));

export default function FileMessage(props: Props) {
  const {name, size, type, isUnreachable, self} = props;

  const {objectURL} = useCreateObjectUrl(
    typeof props.src === 'string' ? null : props.src
  );

  const [modalOpen, setModalOpen] = useState(false);

  const src = typeof props.src === 'string' ? props.src : objectURL;

  const classes = useStyles();

  const sizeFormatted = size ? formatBits(size) : '';

  if (!src) return null;

  return (
    <div className={classes.container} data-self={self}>
      <a
        href={src}
        download
        className={classes.fileButton}
        target="_blank"
        rel="noopener noreferrer"
      >
        <div className={classes.fileWrap}>
          <span className={classes.fileType}>
            <FileTypeIcons type={type} />
          </span>

          <Typography className={classes.fileName}>
            {name || 'Archivo adjunto'}
          </Typography>

          <span className={classes.downloadIcon}>
            <DownloadIcon />
          </span>
        </div>

        <footer className={classes.fileData}>
          {type && <Typography className={classes.fileSize}>{type}</Typography>}
          {sizeFormatted && (
            <Typography className={classes.fileSize}>
              {sizeFormatted}
            </Typography>
          )}
        </footer>
      </a>
      {isUnreachable && (
        <ButtonBase
          className={classes.infoIcon}
          onClick={() => setModalOpen(true)}
        >
          <InfoOutlined fontSize="small" />
        </ButtonBase>
      )}
      <CustomModal open={modalOpen} onClose={() => setModalOpen(false)}>
        <div className={classes.modalContainer}>
          <Typography className={classes.modalTitle}>
            Información limitada
          </Typography>
          <Typography className={classes.modalText}>
            No pudimos obtener los detalles del archivo (nombre, tamaño, tipo).
            Sin embargo, puedes acceder al archivo haciendo clic en el botón de
            descarga.
          </Typography>
        </div>
      </CustomModal>
    </div>
  );
}
